import React from 'react'
import R from 'ramda'
import PropTypes from 'prop-types'
import {
  Input,
  Loader,
  Container,
  Label,
  Icon,
  Segment
} from 'semantic-ui-react'

const getMinWidth = min => R.compose(
  R.max(min),
  R.multiply(8),
  R.defaultTo(0),
  R.path(['acInput', 'length'])
) // (this.state)

const filterSuggestions = (uniqField = '_id') => R.compose(
  R.defaultTo([]),
  // R.difference(R.__, selections),
  R.uniqBy(R.prop(uniqField))
) // this.props.suggestions

const getTargetValue = R.compose(
  R.defaultTo(''),
  R.path(['target', 'value'])
)

export default class AutoComplete extends React.PureComponent {
  xhr = null
  state = {
    highlighted: -1,
    acInput: ''
  }

  addSelection = () => {
    const tmp = this.props.suggestions[this.state.highlighted]
    if (!tmp) return
    const selections = R.uniqBy(R.prop(this.props.uniqField), this.props.selections.concat(tmp))
    this.props.onSelectionChange(selections, tmp) // call with both the full set of selections and the most recent addition
    this.props.onClearSuggestions()
    this.setState({
      highlighted: -1,
      acInput: ''
    })
  }

  handleBlur = (e) => {
    e.preventDefault()
    const state = {
      highlighted: -1,
      acInput: ''
    }
    this.setState(state, () => {
      this.props.onClearSuggestions()
    })
  }

  handleSuggestionClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.addSelection()
  }

  handleSuggestionOver = (e) => {
    const target = e.target.closest('[data-hi]')
    if (R.isNil(target)) {
      return
    }
    const hi = target.getAttribute('data-hi')
    if (R.isNil(hi)) {
      return
    }
    const highlighted = parseInt(hi)
    if (isNaN(highlighted)) {
      return
    }
    this.setState({highlighted})
  }

  getSuggestions = () => filterSuggestions(this.props.uniqField)(this.props.suggestions)
    .map((s, idx) => R.find(R.propEq(this.props.uniqField, R.prop(this.props.uniqField, s)), this.props.selections)
      ? null
      : (<Segment
        style={{cursor: 'pointer', background: 'white'}}
        color={ this.state.highlighted === idx ? 'grey' : null}
        inverted={ this.state.highlighted === idx }
        key={'sug_' + R.prop(this.props.uniqField, s)}
        data-id={R.prop(this.props.uniqField, s)}
        data-hi={idx}
        onMouseDown={this.handleSuggestionClick}
        onMouseEnter={this.handleSuggestionOver}
      >{this.props.formatSuggestion(s, this.state.acInput)}</Segment>
      )
    )

  getSelections = () => this.props.selections.map((s, idx) =>
    (<Label
      key={'sel_' + R.prop(this.props.uniqField, s)}
      data-hi={idx}
      style={{ marginBottom: "4px" }}
    >{this.props.formatSelection(s)}
      <Icon
        name='delete'
        data-id={R.prop(this.props.uniqField, s)}
        onClick={this.onRemove}
      />
    </Label>)
  )

  onRemove = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const dataId = e.target.getAttribute('data-id')
    if (typeof dataId !== 'undefined') {
      const selections = this.props.selections.filter(a => R.prop(this.props.uniqField, a) !== dataId)
      this.props.onSelectionChange(selections)
      this.props.onClearSuggestions()
      this.setState({ highlighted: -1, acInput: '' })
    }
  }

  onTermChange = (event) => {
    const term = getTargetValue(event)
    this.setState({ [event.target.name]: term }, () => {
      this.props.onTermChange({term: term.trim(), pageNum: 1})
    })
  }

  handleKeyDown = (e) => {
    if (e.key) {
      if (e.key.toLowerCase() === 'arrowdown') {
        e.preventDefault()
        const highlighted = this.state.highlighted + 1
        const sugLen = R.pathOr(0, ['suggestions', 'length'], this.props)
        if (highlighted < sugLen) {
          this.setState({highlighted})
        }
      }
      if (e.key.toLowerCase() === 'arrowup') {
        e.preventDefault()
        if (this.state.highlighted > 0) {
          const highlighted = this.state.highlighted - 1
          this.setState({highlighted})
        }
      }
      if (e.key.toLowerCase() === 'enter') {
        this.addSelection()
      }
    }
  }

  handleFetchMore = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.props.onTermChange({term: this.state.acInput, pageNum: this.props.pageNum + 1})
  }

  setFocus = () => this.textInput.focus()

  hasMore = () => R.has('hasMore', this.props)
    ? this.props.hasMore()
    : R.propOr(0, 'total', this.props) - R.pathOr(0, ['suggestions', 'length'], this.props)

  render () {
    const minWidth = getMinWidth(this.props.minWidth || 180)(this.state) + 'px'
    return (
      <div
        style={{
          position: 'relative',
          cursor: 'text',
          padding: '0px',
          zIndex: this.props.zIndex || 1000,
          minWidth,
          width: '100%',
        }}
        className='ui selection dropdown'
        onMouseUp={this.setFocus}
      >
        {this.props.selections.length > 0 ? (
          <div style={{
            display: "inline-block",
            width: "100%",
            padding: "0.2em",
            maxHeight: "16vh",
            overflowY: "scroll",
          }}>
            { this.getSelections() }
          </div>
        ) : null}
        <Container fluid style={{padding: '0px', display: 'inline', minWidth: '300px'}}>
          <Input
            style={{padding: '0.35em'}}
            autoComplete='off'
            icon={this.props.icon || null}
            iconPosition={this.props.icon ? 'left' : null}
            fluid
            placeholder={this.props.placeholder || null}
            size='large'
            transparent
            onChange={this.onTermChange}
            onKeyDown={this.handleKeyDown}
            value={this.state.acInput}
            onBlur={this.handleBlur}
            name='acInput'
            ref={(input) => { this.textInput = input }}
          />
          { (this.props.isLoading || this.props.suggestions.length > 0) ? (
            <Segment.Group
              style={{position: 'absolute', marginTop: '0px', maxHeight: '300px', width: '400px', overflow: 'auto'}}
              onMouseOver={this.handleSuggestionOver}
            >
              { this.getSuggestions() }
              { this.hasMore() || this.props.isLoading
                ? this.props.isLoading
                  ? <Segment>Loading...  <Loader active inline size='mini' /></Segment>
                  : <Segment
                    style={{cursor: 'pointer', background: 'white'}}
                    onMouseDown={this.handleFetchMore}
                    onMouseEnter={this.handleFetchMore}
                  >Load more ...</Segment>
                : null
              }
            </Segment.Group>
          ) : null }
        </Container>
      </div>
    )
  }
}

AutoComplete.propTypes = {
  zIndex: PropTypes.number,
  selections: PropTypes.array.isRequired,
  suggestions: PropTypes.array.isRequired,
  onTermChange: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  onClearSuggestions: PropTypes.func.isRequired,
  hasMore: PropTypes.func,

  minWidth: PropTypes.number,
  icon: PropTypes.string,
  placeholder: PropTypes.string,
  uniqField: PropTypes.string.isRequired,
  formatSelection: PropTypes.func.isRequired,
  formatSuggestion: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  total: PropTypes.number.isRequired,
  pageNum: PropTypes.number.isRequired
}



// WEBPACK FOOTER //
// ./webAdmin/views/autocomplete/index.js